// PracticeTest State Machine - Elite Performance Pattern
// Consolidates 20+ useState hooks into a single predictable state machine
// PRACTICE ONLY: Session and freeze rules apply to practice tests; AI Exam (mock-exam) is untouched.
//
// CRASH-SENSITIVE ZONE: This reducer is the ONLY place allowed to mutate:
//   - questions, currentQuestionIndex, session, progress.
// Handlers must ONLY dispatch actions. Audio/mic must use refs only (no SET_QUESTIONS, RESET_SESSION, or index changes).
//
// INVARIANT: Single authoritative phase. All UI, effects, and handlers must key off phase.
// Forbidden: Audio, mic, skip, follow-ups, CTA must NEVER dispatch SET_QUESTIONS, reset session, or change currentIndex.

import { AILearningInsight } from '../../services/aiAdaptiveLearningService';
import type { ChatMessageData } from '../../types/ChatMessageData';
import { logger } from '../../utils/logger';
import type { Question } from '../../types';

/** PRODUCTION INVARIANT: Log violation, auto-correct, never throw. */
function invariantSoft(label: string, check: boolean, data?: Record<string, unknown>): void {
  if (!check) {
    logger.lifecycle('PracticeTest', 'invariant_violation', { label, ...data });
  }
}

/** PRODUCTION INVARIANT: Critical safety checks - prevent crashes at all costs */
function invariantCritical(label: string, check: boolean, fallback?: () => void, data?: Record<string, unknown>): void {
  if (!check) {
    logger.lifecycle('PracticeTest', 'critical_invariant_violation', { label, ...data });
    // Emergency fallback to prevent crash
    if (fallback) {
      try {
        fallback();
      } catch (fallbackError) {
        logger.lifecycle('PracticeTest', 'fallback_failed', { label, fallbackError: String(fallbackError) });
      }
    }
  }
}

/** PRODUCTION GUARD: Ensure array access is safe */
function safeArrayAccess<T>(arr: T[], index: number, fallback?: T): T | undefined {
  if (!Array.isArray(arr) || arr.length === 0) {
    logger.lifecycle('PracticeTest', 'safe_array_access_invalid_array', { length: arr?.length });
    return fallback;
  }
  if (index < 0 || index >= arr.length) {
    logger.lifecycle('PracticeTest', 'safe_array_access_out_of_bounds', { index, length: arr.length });
    return fallback;
  }
  return arr[index];
}

/** PRODUCTION GUARD: Ensure question access is safe */
function safeQuestionAccess(questions: Question[], index: number): Question | null {
  const question = safeArrayAccess(questions, index);
  if (!question) {
    logger.lifecycle('PracticeTest', 'safe_question_access_failed', { index, questionsLength: questions.length });
    return null;
  }
  // Validate question structure
  if (!question.id || !question.text) {
    logger.lifecycle('PracticeTest', 'invalid_question_structure', { questionId: question.id, hasText: !!question.text });
    return null;
  }
  return question;
}

/** Authoritative practice test phase. All rendering and handlers key off this. No implicit booleans. */
export type PracticeTestPhase =
  | 'question'   // Showing question, waiting for answer
  | 'answering' // User submitted answer; brief transition to explanation
  | 'explanation' // Showing explanation + follow-ups
  | 'advancing' // Transitioning to next question (e.g. after Next clicked)
  | 'terminal'; // Test complete

/** Derive phase from state (for use outside reducer). */
export function getPhaseFromState(state: PracticeTestState): PracticeTestPhase {
  if (state.testComplete) return 'terminal';
  if (state.phase === 'advancing') return 'advancing';
  if (state.isAnswered && state.showExplanation) return 'explanation';
  if (state.isAnswered && !state.showExplanation) return 'answering';
  return 'question';
}

/** Practice test session: sealed once started. Question set, order, and count never change until test ends. */
export interface PracticeTestSessionAnswer {
  questionId: string;
  selectedAnswerId: string;
  isCorrect: boolean;
}

export interface PracticeTestSession {
  sessionQuestions: Question[];
  currentIndex: number;
  answers: PracticeTestSessionAnswer[];
  startedAt: number;
  completedAt: number | null;
  isFrozen: boolean;
}

export interface PracticeTestState {
  /** Authoritative phase. All UI, effects, and handlers must key off this. No implicit booleans. */
  phase?: PracticeTestPhase;
  /** Practice only: sealed session; null for mock-exam or before first load. */
  session: PracticeTestSession | null;
  // Core test state (derived from session when session exists; used directly for mock-exam)
  questions: Question[];
  currentQuestionIndex: number;
  selectedAnswer: string | null;
  isAnswered: boolean;
  showExplanation: boolean;
  score: number;
  streak: number; // Consecutive correct answers
  testComplete: boolean;
  showHint: boolean; // Proactive hint for hesitating users
  preQuestionStage: 'context' | 'keyRule' | 'checkpoint' | 'ready';
  preQuestionCardFading: boolean;
  /** Tap stability: answer inputs disabled until this timestamp (ms) to prevent double-tap. */
  answerInputDisabledUntil: number;
  isUpdatingSuggestions: boolean; // Loading state for card regeneration

  // Audio state
  isMuted: boolean;
  timeLeft: number;

  // Accessibility
  isAccessibilityMode: boolean;
  
  // Learning & difficulty
  currentDifficultyLevel: number;
  questionStartTime: number;
  aiInsight: AILearningInsight | null;
  recentPerformance: {
    correct: number;
    total: number;
    averageTime: number;
  };
  
  // Chat & follow-up state
  chatMessages: ChatMessageData[];
  followUpCount: number;
  isCooldown: boolean;
  cooldownTimeRemaining: number;
  storedMotivationWord: string;
  followUpSuggestions: string[];
  lockedMisconceptionId: string | null; // STRICT: Locked misconception ID after first card selection
  
  // UI state
  isTranslating: boolean;
  showSoftGate: boolean;
  showSubscriptionGate: boolean;
  showHardGate: boolean;
  precomputedNextTest: { id: string; name: string } | null;
}

export type Action = PracticeTestAction;
export type PracticeTestAction =
  | { type: 'SELECT_ANSWER'; payload: { answerId: string; isCorrect: boolean; motivationWord: string } }
  | { type: 'SHOW_EXPLANATION' }
  | { type: 'NEXT_QUESTION'; payload: { questionStartTime: number } }
  | { type: 'COMPLETE_TEST' }
  | { type: 'SET_QUESTIONS'; payload: Question[] | { questions: Question[]; isPractice: true } }
  | { type: 'SET_SCORE'; payload: number }
  | { type: 'SET_MUTED'; payload: boolean }
  | { type: 'SET_TIME_LEFT'; payload: number }
  | { type: 'SET_ACCESSIBILITY_MODE'; payload: boolean }
  | { type: 'SET_DIFFICULTY_LEVEL'; payload: number }
  | { type: 'SET_AI_INSIGHT'; payload: AILearningInsight | null }
  | { type: 'SET_RECENT_PERFORMANCE'; payload: { correct: number; total: number; averageTime: number } }
  | { type: 'ADD_CHAT_MESSAGE'; payload: ChatMessageData }
  | { type: 'REPLACE_CHAT_MESSAGE'; payload: ChatMessageData }
  | { type: 'SET_CHAT_MESSAGES'; payload: ChatMessageData[] }
  | { type: 'SET_FOLLOW_UP_COUNT'; payload: number }
  | { type: 'SET_FOLLOW_UP_SUGGESTIONS'; payload: string[] }
  | { type: 'SET_UPDATING_SUGGESTIONS'; payload: boolean }
  | { type: 'LOCK_MISCONCEPTION'; payload: string } // STRICT: Lock misconception ID after first card selection
  | { type: 'SET_COOLDOWN'; payload: { isCooldown: boolean; timeRemaining?: number } }
  | { type: 'SET_MOTIVATION_WORD'; payload: string }
  | { type: 'SET_TRANSLATING'; payload: boolean }
  | { type: 'SET_SOFT_GATE'; payload: boolean }
  | { type: 'SET_SUBSCRIPTION_GATE'; payload: boolean }
  | { type: 'SET_HARD_GATE'; payload: boolean }
  | { type: 'SET_PRECOMPUTED_NEXT_TEST'; payload: { id: string; name: string } | null }
  | { type: 'SET_SHOW_HINT'; payload: boolean }
  | { type: 'SET_PRE_QUESTION_STAGE'; payload: 'context' | 'keyRule' | 'checkpoint' | 'ready' }
  | { type: 'START_PRE_QUESTION_FADE' }
  | { type: 'SET_ANSWER_INPUT_DISABLED_UNTIL'; payload: number }
  | { type: 'RESET_QUESTION_STATE' }
  | { type: 'RESET_SESSION' }
  | { type: 'CLEAR_PRACTICE_SESSION' }
  | { type: 'EXIT_TEST' };

export const initialState: PracticeTestState = {
  phase: 'question',
  session: null,
  questions: [],
  currentQuestionIndex: 0,
  selectedAnswer: null,
  isAnswered: false,
  showExplanation: false,
  score: 0,
  streak: 0,
  testComplete: false,
  showHint: false,
  preQuestionStage: 'ready',
  preQuestionCardFading: false,
  answerInputDisabledUntil: 0,
  isMuted: false,
  timeLeft: 0,
  isAccessibilityMode: false,
  currentDifficultyLevel: 3,
  questionStartTime: Date.now(),
  aiInsight: null,
  recentPerformance: { correct: 0, total: 0, averageTime: 0 },
  chatMessages: [],
  followUpCount: 0,
  isCooldown: false,
  cooldownTimeRemaining: 0,
  storedMotivationWord: '',
  followUpSuggestions: [],
  isUpdatingSuggestions: false,
  lockedMisconceptionId: null, // STRICT: Locked misconception ID after first card selection
  isTranslating: false,
  showSoftGate: false,
  showSubscriptionGate: false,
  showHardGate: false,
  precomputedNextTest: null,
};

function freezeInDev<T extends object>(s: T): T {
  if (process.env.NODE_ENV === 'development' && s != null) {
    try {
      Object.freeze(s);
    } catch (_) {
      // Ignore if already frozen or not extensible
    }
  }
  return s;
}

export function practiceTestReducer(
  state: PracticeTestState,
  action: PracticeTestAction
): PracticeTestState {
  switch (action.type) {
    case 'SELECT_ANSWER': {
      // PRODUCTION GUARD: Ensure safe question access
      const qs = state.session?.sessionQuestions ?? state.questions;
      const idx = state.session?.currentIndex ?? state.currentQuestionIndex;
      const currentQuestion = safeQuestionAccess(qs, idx);

      // CRITICAL: If no valid question, do not proceed - prevent crash
      if (!currentQuestion) {
        logger.lifecycle('PracticeTest', 'select_answer_no_question', { idx, len: qs?.length });
        invariantCritical('SELECT_ANSWER_no_question', false, undefined, { idx, len: qs?.length });
        return state; // Return current state to prevent crash
      }

      invariantSoft('SELECT_ANSWER_index', idx >= 0 && idx < (qs?.length ?? 0), { idx, len: qs?.length });
      return {
        ...state,
        phase: 'explanation',
        selectedAnswer: action.payload.answerId,
        isAnswered: true,
        showExplanation: true,
        score: action.payload.isCorrect ? state.score + 1 : state.score,
        streak: action.payload.isCorrect ? state.streak + 1 : 0,
        storedMotivationWord: action.payload.motivationWord,
        answerInputDisabledUntil: Date.now() + 150, // Tap stability: prevent double-tap for 150ms
      };
    }

    case 'SHOW_EXPLANATION':
      return {
        ...state,
        showExplanation: true,
      };

    case 'NEXT_QUESTION': {
      const questions = state.session?.sessionQuestions ?? state.questions;
      const len = questions.length;
      if (len === 0) {
        logger.lifecycle('PracticeTest', 'next_question_no_questions', { len });
        return state;
      }
      const currentIdx = state.session?.currentIndex ?? state.currentQuestionIndex;
      
      // PRODUCTION GUARD: Safe index handling
      const safeCurrentIdx = typeof currentIdx === 'number' && currentIdx >= 0 && currentIdx < len 
        ? currentIdx 
        : Math.min(Math.max(0, typeof currentIdx === 'number' ? currentIdx : 0), len - 1);
      
      const nextIndex = Math.min(safeCurrentIdx + 1, len - 1);
      const isLast = safeCurrentIdx >= len - 1;

      // PRODUCTION GUARD: Validate current question before proceeding
      const currentQuestion = safeQuestionAccess(questions, safeCurrentIdx);
      if (!currentQuestion && !isLast) {
        logger.lifecycle('PracticeTest', 'next_question_invalid_current', { safeCurrentIdx, len });
        // Continue to next question anyway, but log the issue
      }

      if (state.session?.isFrozen && !isLast) {
        const currentQ = questions[safeCurrentIdx];
        const newAnswers = [...state.session.answers];
        if (currentQ && state.selectedAnswer) {
          newAnswers.push({
            questionId: currentQ.id,
            selectedAnswerId: state.selectedAnswer,
            isCorrect: state.selectedAnswer === currentQ.correctAnswerId,
          });
        }
        const nextSession: PracticeTestSession = {
          ...state.session,
          currentIndex: nextIndex,
          answers: newAnswers,
        };
        return {
          ...state,
          phase: 'question' as PracticeTestPhase,
          session: nextSession,
          currentQuestionIndex: nextIndex,
          selectedAnswer: null,
          isAnswered: false,
          showExplanation: false,
          preQuestionStage: 'context',
          preQuestionCardFading: false,
          questionStartTime: action.payload.questionStartTime,
          chatMessages: [],
          followUpCount: 0,
          followUpSuggestions: [],
          lockedMisconceptionId: null,
          isCooldown: false,
          cooldownTimeRemaining: 0,
        };
      }

      if (!state.session && !isLast) {
        return {
          ...state,
          phase: 'question' as PracticeTestPhase,
          currentQuestionIndex: nextIndex,
          selectedAnswer: null,
          isAnswered: false,
          showExplanation: false,
          preQuestionStage: 'ready',
          preQuestionCardFading: false,
          questionStartTime: action.payload.questionStartTime,
          chatMessages: [],
          followUpCount: 0,
          followUpSuggestions: [],
          lockedMisconceptionId: null,
          isCooldown: false,
          cooldownTimeRemaining: 0,
        };
      }

      if (state.session?.isFrozen && isLast && state.selectedAnswer && currentQuestion) {
        const newAnswers = [...state.session.answers, {
          questionId: currentQuestion.id,
          selectedAnswerId: state.selectedAnswer,
          isCorrect: state.selectedAnswer === currentQuestion.correctAnswerId,
        }];
        return {
          ...state,
          phase: 'terminal' as PracticeTestPhase,
          session: { ...state.session, answers: newAnswers, completedAt: Date.now(), isFrozen: false },
          testComplete: true,
        };
      }

      return { ...state, testComplete: true };
    }

    case 'COMPLETE_TEST': {
      if (state.session?.isFrozen) {
        return {
          ...state,
          phase: 'terminal',
          session: {
            ...state.session,
            completedAt: Date.now(),
            isFrozen: false,
          },
          testComplete: true,
        };
      }
      return { ...state, phase: 'terminal', testComplete: true };
    }

    case 'RESET_SESSION':
      return {
        ...initialState,
        phase: 'question',
        session: null,
        questions: [],
        currentQuestionIndex: 0,
        questionStartTime: Date.now(),
        isMuted: state.isMuted,
        isAccessibilityMode: state.isAccessibilityMode,
      };

    case 'CLEAR_PRACTICE_SESSION':
      return {
        ...initialState,
        phase: 'question',
        session: null,
        questions: [],
        currentQuestionIndex: 0,
        questionStartTime: Date.now(),
        isMuted: state.isMuted,
        isAccessibilityMode: state.isAccessibilityMode,
      };

    // CRASH-SENSITIVE: Only reducer may set questions/session/index. Audio/mic must never dispatch this.
    case 'SET_QUESTIONS': {
      const raw = action.payload;
      const payload = Array.isArray(raw) ? raw : (raw as { questions: Question[]; isPractice?: boolean }).questions;
      const isPractice = !Array.isArray(raw) && (raw as { isPractice?: boolean }).isPractice === true;
      const nextLength = payload?.length ?? 0;
      const clampedIndex = nextLength > 0 ? Math.min(state.currentQuestionIndex, Math.max(0, nextLength - 1)) : 0;

      if (isPractice && state.session?.isFrozen) {
        logger.lifecycle('PracticeTest', 'set_questions_ignored_frozen_session', {
          reason: 'Loader/cache/top-up fired mid-test; question set cannot change until test ends',
        });
        return state;
      }
      if (isPractice && nextLength > 0) {
        const session: PracticeTestSession = {
          sessionQuestions: payload,
          currentIndex: 0,
          answers: [],
          startedAt: Date.now(),
          completedAt: null,
          isFrozen: true,
        };
        const next: PracticeTestState = {
          ...state,
          phase: 'question' as PracticeTestPhase,
          session,
          questions: payload,
          currentQuestionIndex: 0,
          preQuestionStage: 'context',
          preQuestionCardFading: false,
        };
        return freezeInDev(next);
      }
      if (!isPractice) {
        const nextPreStage: 'context' | 'ready' = 'ready';
        const nextPhase: PracticeTestPhase | undefined = nextLength > 0 ? ('question' as PracticeTestPhase) : state.phase;
        const next: PracticeTestState = {
          ...state,
          phase: nextPhase,
          questions: payload,
          currentQuestionIndex: clampedIndex,
          preQuestionStage: nextPreStage,
          preQuestionCardFading: false,
        };
        return freezeInDev(next);
      }
      return state;
    }

    case 'SET_SCORE':
      return {
        ...state,
        score: action.payload,
      };

    case 'SET_MUTED':
      return {
        ...state,
        isMuted: action.payload,
      };

    case 'SET_TIME_LEFT':
      return {
        ...state,
        timeLeft: action.payload,
      };

    case 'SET_ACCESSIBILITY_MODE':
      return {
        ...state,
        isAccessibilityMode: action.payload,
      };

    case 'SET_DIFFICULTY_LEVEL':
      return {
        ...state,
        currentDifficultyLevel: action.payload,
      };

    case 'SET_AI_INSIGHT':
      return {
        ...state,
        aiInsight: action.payload,
      };

    case 'SET_RECENT_PERFORMANCE':
      return {
        ...state,
        recentPerformance: action.payload,
      };

    case 'ADD_CHAT_MESSAGE': {
      // INVARIANT: No duplicate message ids
      if (state.chatMessages.some(msg => msg.id === action.payload.id)) {
        return state;
      }
      // PRODUCTION INVARIANT: Strict 2-message cap after follow-up round 1
      // After follow-up round 1, ONLY TWO messages may render: explanation + "Move on / Continue"
      const payload = action.payload;
      const questionId = payload.questionId;
      const isCoach = payload.role === 'coach';
      let nextMessages: ChatMessageData[] = [...state.chatMessages, payload];
      if (isCoach && questionId) {
        const coachForQ = nextMessages.filter(m => m.questionId === questionId && m.role === 'coach');
        // Check if we're in follow-up phase (follow-up count > 0)
        const isInFollowUpPhase = state.followUpCount > 0;
        if (isInFollowUpPhase && coachForQ.length > 2) {
          // PRODUCTION: Log violation and auto-correct - never crash
          logger.lifecycle('PracticeTest', 'critical_follow_up_violation', {
            questionId,
            count: coachForQ.length,
            followUpCount: state.followUpCount,
            action: 'emergency_trim_to_2',
            violatingMessageId: payload.id
          });
          // Keep only the last 2 messages (explanation + continue)
          const keepIds = new Set(coachForQ.slice(-2).map(m => m.id));
          nextMessages = nextMessages.filter(
            m => m.questionId !== questionId || m.role !== 'coach' || keepIds.has(m.id)
          );
        }
        // Additional safety: always cap to 2 to prevent any regression
        if (coachForQ.length > 2) {
          logger.lifecycle('PracticeTest', 'follow_up_cap_prevention', {
            questionId,
            count: coachForQ.length,
            action: 'preventive_trim'
          });
          const keepIds = new Set(coachForQ.slice(-2).map(m => m.id));
          nextMessages = nextMessages.filter(
            m => m.questionId !== questionId || m.role !== 'coach' || keepIds.has(m.id)
          );
        }
      }
      return {
        ...state,
        chatMessages: nextMessages,
      };
    }
    case 'REPLACE_CHAT_MESSAGE':
      return {
        ...state,
        chatMessages: state.chatMessages.map(msg =>
          msg.id === action.payload.id ? action.payload : msg
        ),
      };
    case 'SET_CHAT_MESSAGES':
      return {
        ...state,
        chatMessages: action.payload,
      };

    case 'SET_FOLLOW_UP_COUNT':
      return {
        ...state,
        followUpCount: action.payload,
      };
    case 'SET_FOLLOW_UP_SUGGESTIONS':
      return {
        ...state,
        followUpSuggestions: action.payload as string[],
        isUpdatingSuggestions: false, // Clear loading state when suggestions are set
      };
    
    case 'SET_UPDATING_SUGGESTIONS':
      return {
        ...state,
        isUpdatingSuggestions: action.payload,
      };

    case 'SET_COOLDOWN':
      return {
        ...state,
        isCooldown: action.payload.isCooldown,
        cooldownTimeRemaining: action.payload.timeRemaining ?? state.cooldownTimeRemaining,
      };

    case 'SET_MOTIVATION_WORD':
      return {
        ...state,
        storedMotivationWord: action.payload,
      };

    case 'SET_TRANSLATING':
      return {
        ...state,
        isTranslating: action.payload,
      };

    case 'SET_SOFT_GATE':
      return {
        ...state,
        showSoftGate: action.payload,
      };

    case 'SET_SUBSCRIPTION_GATE':
      return {
        ...state,
        showSubscriptionGate: action.payload,
      };

    case 'SET_HARD_GATE':
      return {
        ...state,
        showHardGate: action.payload,
      };

    case 'SET_SHOW_HINT':
      return {
        ...state,
        showHint: action.payload,
      };

    case 'SET_PRE_QUESTION_STAGE':
      return {
        ...state,
        preQuestionStage: action.payload,
        preQuestionCardFading: false,
      };

    case 'START_PRE_QUESTION_FADE':
      return {
        ...state,
        preQuestionCardFading: true,
      };

    case 'SET_ANSWER_INPUT_DISABLED_UNTIL':
      return {
        ...state,
        answerInputDisabledUntil: action.payload,
      };

    case 'SET_PRECOMPUTED_NEXT_TEST':
      return {
        ...state,
        precomputedNextTest: action.payload,
      };

    case 'RESET_QUESTION_STATE':
      return {
        ...state,
        phase: 'question',
        selectedAnswer: null,
        isAnswered: false,
        showExplanation: false,
        preQuestionStage: 'context',
        preQuestionCardFading: false,
        followUpCount: 0,
        lockedMisconceptionId: null,
        isCooldown: false,
        cooldownTimeRemaining: 0,
      };

    case 'EXIT_TEST':
      return {
        ...initialState,
        phase: 'question',
        session: null,
        questions: [],
        currentQuestionIndex: 0,
        questionStartTime: Date.now(),
        isMuted: state.isMuted,
        isAccessibilityMode: state.isAccessibilityMode,
      };

    default:
      return state;
  }
}
